import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareAntiExploitSystemWideDetectionsWindowsAction,
  AntimalwareAntiExploitSystemWideDetectionsLinuxAction,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  systemWideDetectionsWindowsColumns,
  AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey,
  convertSystemWideDetectionsWindowsDbValueToFormValue,
  convertSystemWideDetectionsWindowsFormValueToDbValue,
  systemWideDetectionsWindowsRows,
  AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey,
  convertSystemWideDetectionsLinuxDbValueToFormValue,
  convertSystemWideDetectionsLinuxFormValueToDbValue,
  systemWideDetectionsLinuxColumns,
  systemWideDetectionsLinuxRows,
} from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../ui/gzSelectableTableRowGroup';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../ui/gzButton/types';
import {
  ANTIMALWARE_ANTI_EXPLOIT_DEFAULT_VALUE,
  ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_LINUX_DEFAULT_VALUE,
  ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_WINDOWS_DEFAULT_VALUE,
  getAntimalwareAntiExploitSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import {
  AntimalwareAntiExploitFormValue,
  AntimalwareAntiExploitSearchableKeywords,
  AntimalwareAntiExploitUiApplication,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { InfoBoxSeverity } from '../../../../../../../ui/gzInfoBox/types';
import { Feature } from '@nirvana/shared/constants';
import { GzAntimalwareAntiExploitApplicationsTableWebComponent } from '../gridComponents/predefinedApplications/GzAntimalwareAntiExploitApplicationsTableWebComponent';
import { convertDbApplicationToUiApplication, convertUiApplicationToDbApplication } from '../utils/converters';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT)
export class GzAntimalwareAntiExploitWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareAntiExploitFormValue,
  AntimalwareAntiExploitSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_ANTI_EXPLOIT,
  };
  protected searchableKeywords = getAntimalwareAntiExploitSearchableKeywords();

  @query('#antimalware-anti-exploit-enable-field')
  private antimalwareAntiExploitEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-anti-exploit-info-text')
  private antimalwareAntiExploitInfoText: GzTextWebComponent;
  @query('#antimalware-anti-exploit-content')
  private antimalwareAntiExploitContent: HTMLElement;
  @query('#antimalware-anti-exploit-system-wide-detections-windows-field')
  private antimalwareAntiExploitSystemWideDetectionsWindowsField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<
      AntimalwareAntiExploitSystemWideDetectionsWindowsLevelKey,
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction
    >
  >;
  @query('#antimalware-anti-exploit-system-wide-detections-windows-info-box')
  private antimalwareAntiExploitSystemWideDetectionsWindowsInfoBox: GzInfoBoxWebComponent;
  @query('#antimalware-anti-exploit-system-wide-detections-linux-field')
  private antimalwareAntiExploitSystemWideDetectionsLinuxField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<
      AntimalwareAntiExploitSystemWideDetectionsLinuxLevelKey,
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction
    >
  >;
  @query('#antimalware-anti-exploit-predefined-applications-field')
  private antimalwareAntiExploitPredefinedApplicationsField: GzFormFieldWebComponent<
    GzAntimalwareAntiExploitApplicationsTableWebComponent,
    AntimalwareAntiExploitUiApplication[]
  >;
  @query('#antimalware-anti-exploit-custom-applications-field')
  private antimalwareAntiExploitCustomApplicationsField: GzFormFieldWebComponent<
    GzAntimalwareAntiExploitApplicationsTableWebComponent,
    AntimalwareAntiExploitUiApplication[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="antimalware-anti-exploit-enable-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.AdvancedAntiExploit]
          .label}"
        gz-form-input
        @toggleChange="${e => {
          toggleDisabled(this.antimalwareAntiExploitInfoText, !e.detail.checked);
          toggleDisabled(this.antimalwareAntiExploitContent, !e.detail.checked);
        }}"
      >
        <span
          >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.AdvancedAntiExploit]
            .translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field> `;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="antimalware-anti-exploit-info-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        'Advanced Anti-exploit provides on-execution protection against attacks that target vulnerabilities in common applications, such as browsers, Microsoft Office, and Adobe Reader. It also defends against kernel-level threats that occur after an exploit has been executed.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-anti-exploit-content">
        <!-- System Wide Detections -->
        <div id="antimalware-anti-exploit-system-wide-detections">
          <div class="title-with-button">
            <gz-title
              fontSize="${FontSizes.PARAGRAPH_11}"
              fontWeight="${FontWeights.SEMIBOLD}"
              class="form-subtitle"
              searchable-label="${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.SystemWideDetections]
                .label}"
              >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.SystemWideDetections]
                .translatedLabel}</gz-title
            >

            <gz-button
              id="antimalware-anti-exploit-system-wide-detections-reset-button"
              type="${ButtonTypes.QUIET}"
              buttonHeight="${ButtonHeights.SMALL}"
              buttonFontSize="${ButtonFontSizes.SMALL}"
              @click="${this.onResetSystemWideDetectionsButtonClick.bind(this)}"
              >${translateFn('Reset to default')}
            </gz-button>
          </div>

          <!-- System Wide Detections Windows -->
          <div id="antimalware-anti-exploit-system-wide-detections-windows">
            <gz-title
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="mt-2"
              searchable-label="${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.WindowsDetections]
                .label}"
              >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.WindowsDetections]
                .translatedLabel}</gz-title
            >

            <gz-form-field
              class="label-mw-250 mt-2"
              id="antimalware-anti-exploit-system-wide-detections-windows-field"
              label=""
              searchable-label="${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.ProcessIntrospection]
                .label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.PrivilegeEscalation].label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.LsassProtection].label}"
            >
              <gz-selectable-table-row-group
                gz-form-input
                allowFullRowSelection
                .defaultColumn=${AntimalwareAntiExploitSystemWideDetectionsWindowsAction.REPORT_ONLY}
                .rows=${systemWideDetectionsWindowsRows(
                  this.searchableKeywords,
                  !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])
                )}
                .columns=${systemWideDetectionsWindowsColumns()}
                @tableRowGroupSelectionChange="${this.onSystemWideDetectionsWindowsSelectionChange.bind(this)}"
              ></gz-selectable-table-row-group>
            </gz-form-field>

            <gz-info-box
              id="antimalware-anti-exploit-system-wide-detections-windows-info-box"
              severity="${InfoBoxSeverity.WARNING}"
              class="hidden mt-2"
              .innerHTML=${translateFn(
                'We recommend using {bold}Block only{/bold} for this feature. Enabling reporting may generate a large number of notifications, including for processes requesting more permissions than needed.',
                {
                  bold: '<strong>',
                  '/bold': '</strong>',
                }
              )}
            ></gz-info-box>
          </div>

          <!-- System Wide Detections Linux -->
          <div id="antimalware-anti-exploit-system-wide-detections-linux">
            <gz-title
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="mt-2"
              searchable-label="${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.LinuxDetections]
                .label}"
              >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.LinuxDetections]
                .translatedLabel}</gz-title
            >

            <gz-form-field
              class="label-mw-250 mt-2"
              id="antimalware-anti-exploit-system-wide-detections-linux-field"
              label=""
              searchable-label="${this.searchableKeywords[
                AntimalwareAntiExploitSearchableKeywords.CredentialsMonitoring
              ].label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.PtraceMonitoring].label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.NamespaceMonitoring].label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.CorruptionMonitoring].label +
              this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.SuidMonitoring].label}"
            >
              <gz-selectable-table-row-group
                gz-form-input
                allowFullRowSelection
                allowFullColumnSelection
                .defaultColumn=${AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY}
                .rows=${systemWideDetectionsLinuxRows(this.searchableKeywords)}
                .columns=${systemWideDetectionsLinuxColumns()}
              ></gz-selectable-table-row-group>
            </gz-form-field>
          </div>
        </div>

        <!-- Predefined Applications -->
        <div id="antimalware-anti-exploit-system-predefined-applications">
          <div class="title-with-button">
            <gz-title
              fontSize="${FontSizes.PARAGRAPH_11}"
              fontWeight="${FontWeights.SEMIBOLD}"
              class="form-subtitle"
              searchable-label="${this.searchableKeywords[
                AntimalwareAntiExploitSearchableKeywords.PredefinedWindowsApplications
              ].label}"
              >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.PredefinedWindowsApplications]
                .translatedLabel}</gz-title
            >

            <gz-button
              id="antimalware-anti-exploit-reset-button"
              type="${ButtonTypes.QUIET}"
              buttonHeight="${ButtonHeights.SMALL}"
              buttonFontSize="${ButtonFontSizes.SMALL}"
              @click="${this.onResetPredefinedApplicationsButtonClick.bind(this)}"
              >${translateFn('Reset to default')}
            </gz-button>
          </div>

          <gz-form-field id="antimalware-anti-exploit-predefined-applications-field" class="mt-2">
            <gz-policy-panel-antimalware-anti-exploit-apps-table
              class="hideButtonsOutsideGrid"
              predefined
              gz-form-input
            ></gz-policy-panel-antimalware-anti-exploit-apps-table>
          </gz-form-field>
        </div>

        <!-- Custom Applications -->
        <div id="antimalware-anti-exploit-system-predefined-applications">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="form-subtitle mt-4"
            searchable-label="${this.searchableKeywords[
              AntimalwareAntiExploitSearchableKeywords.CustomWindowsApplications
            ].label}"
            >${this.searchableKeywords[AntimalwareAntiExploitSearchableKeywords.CustomWindowsApplications]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="antimalware-anti-exploit-custom-applications-field">
            <gz-policy-panel-antimalware-anti-exploit-apps-table
              gz-form-input
            ></gz-policy-panel-antimalware-anti-exploit-apps-table>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private onSystemWideDetectionsWindowsSelectionChange(event: CustomEvent): void {
    const isInfoBoxVisible =
      event.detail.lsassMemoryAccessFromUnknownProcess.column !==
      AntimalwareAntiExploitSystemWideDetectionsWindowsAction.BLOCK_PROCESS;
    toggleVisibility(this.antimalwareAntiExploitSystemWideDetectionsWindowsInfoBox, isInfoBoxVisible);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.antiExploit
   */
  protected getValueFromManager(): AntimalwareAntiExploitFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_ANTI_EXPLOIT]: ANTIMALWARE_ANTI_EXPLOIT_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_ANTI_EXPLOIT]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.antiExploit'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          antiExploit: {
            enable: this.antimalwareAntiExploitEnableField,
            predefinedApplications: this.antimalwareAntiExploitPredefinedApplicationsField,
            customApplications: this.antimalwareAntiExploitCustomApplicationsField,
            systemWideDetections: this.antimalwareAntiExploitSystemWideDetectionsWindowsField,
            linux: {
              systemWideDetections: this.antimalwareAntiExploitSystemWideDetectionsLinuxField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          antiExploit: {
            enable: this.valueT.uiSettings.antimalware.antiExploit.enable,
            predefinedApplications: this.valueT.uiSettings.antimalware.antiExploit.predefinedApplications.map(
              convertDbApplicationToUiApplication
            ),
            customApplications: this.valueT.uiSettings.antimalware.antiExploit.customApplications.map(
              convertDbApplicationToUiApplication
            ),
            systemWideDetections: convertSystemWideDetectionsWindowsDbValueToFormValue(
              this.valueT.uiSettings.antimalware.antiExploit.systemWideDetections
            ),
            linux: {
              systemWideDetections: convertSystemWideDetectionsLinuxDbValueToFormValue(
                this.valueT.uiSettings.antimalware.antiExploit.linux.systemWideDetections
              ),
            },
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // set system wide detections - windows
    let systemWideDetections;
    systemWideDetections = _.get(value, ['uiSettings', 'antimalware', 'antiExploit', 'systemWideDetections'], {});
    systemWideDetections = convertSystemWideDetectionsWindowsFormValueToDbValue(systemWideDetections);
    _.set(value, ['uiSettings', 'antimalware', 'antiExploit', 'systemWideDetections'], systemWideDetections);

    // set system wide detections - linux
    systemWideDetections = _.get(
      value,
      ['uiSettings', 'antimalware', 'antiExploit', 'linux', 'systemWideDetections'],
      {}
    );
    systemWideDetections = convertSystemWideDetectionsLinuxFormValueToDbValue(systemWideDetections);
    _.set(value, ['uiSettings', 'antimalware', 'antiExploit', 'linux', 'systemWideDetections'], systemWideDetections);

    // set predefined applications
    const predefinedApplications = _.get(
      value,
      ['uiSettings', 'antimalware', 'antiExploit', 'predefinedApplications'],
      []
    );
    _.set(
      value,
      ['uiSettings', 'antimalware', 'antiExploit', 'predefinedApplications'],
      predefinedApplications.map(convertUiApplicationToDbApplication)
    );

    // set custom applications
    const customApplications = _.get(value, ['uiSettings', 'antimalware', 'antiExploit', 'customApplications'], []);
    _.set(
      value,
      ['uiSettings', 'antimalware', 'antiExploit', 'customApplications'],
      customApplications.map(convertUiApplicationToDbApplication)
    );

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'antiExploit'], {});
    }

    return value;
  }

  /**
   * Reset system wide detections to default value.
   */
  private onResetSystemWideDetectionsButtonClick() {
    this.antimalwareAntiExploitSystemWideDetectionsWindowsField.value =
      convertSystemWideDetectionsWindowsDbValueToFormValue(
        ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_WINDOWS_DEFAULT_VALUE
      );
    this.antimalwareAntiExploitSystemWideDetectionsLinuxField.value =
      convertSystemWideDetectionsLinuxDbValueToFormValue(
        ANTIMALWARE_ANTI_EXPLOIT_SYSTEM_WIDE_DETECTIONS_LINUX_DEFAULT_VALUE
      );
  }

  /**
   * Reset predefined applications to default value.
   */
  onResetPredefinedApplicationsButtonClick() {
    this.antimalwareAntiExploitPredefinedApplicationsField.Input.resetValues();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT: GzAntimalwareAntiExploitWebComponent;
  }
}
